import {createSelector} from 'reselect';
import omit from 'lodash/omit';
import {Component, Plugins} from '@packages/systems/wfdl';
import {
  getLiveComponentMap,
  selectCurrentComponent,
  selectCurrentComponentType,
  selectIsUserComponentFocused,
} from '@packages/utilities/platform-selectors';
import {selectCurrentComponentCanvasPage} from '@packages/systems/component-canvas/universal';
import {getName, qualify} from '@packages/systems/wfdl-Qualified';
import {hashCode} from '@packages/utilities/string-utils';
import {getFeatureFlagOutsideOfReact} from '@packages/systems/feature-config/client';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';
import type {DesignerStateType} from '@packages/systems/core/records/DesignerState';
import type {ComponentStateScenario} from '@packages/systems/code-libraries/component-states';
import {weakMemo} from '@packages/utilities/memo';
import {getVariantPropNames} from './flux/componentPreviewScenarioUpdate';

export {
  selectIsEditingCodeComponent,
  selectIsNotEditingCodeComponent,
} from '@packages/utilities/platform-selectors';

const EMPTY_SCENARIO_LIST: ComponentStateScenario[] = [];

// States stored before the store stripped Variant props on save still carry
// them, and a Variant value would force every grid column to one variant.
// Strip them on read too, without merging states, so each keeps its frame.
const withoutVariantProps = weakMemo((scenarios: ComponentStateScenario[]) =>
  weakMemo((variants: Component.Variants): ComponentStateScenario[] => {
    const names = [...getVariantPropNames(variants)];
    const hasVariantProp = (scenario: ComponentStateScenario) =>
      names.some((name) => scenario.props && name in scenario.props);
    if (!scenarios.some(hasVariantProp)) return scenarios;
    return scenarios.map((scenario) =>
      hasVariantProp(scenario)
        ? {...scenario, props: Object.freeze(omit(scenario.props, names))}
        : scenario
    );
  })
);

/**
 * Returns the multi-state preview scenarios stored for a component id, or
 * an empty array when none are stored.
 */
export const selectScenariosForComponent = (
  state: {DesignerStore: DesignerStateType},
  componentId: string | null | undefined
): ComponentStateScenario[] => {
  if (!componentId) return EMPTY_SCENARIO_LIST;
  if (!getFeatureFlagOutsideOfReact(FEATURE_FLAGS.AICC_VISUAL_EDITING_STATES)) {
    return EMPTY_SCENARIO_LIST;
  }
  const componentMap = getLiveComponentMap(state.DesignerStore);
  const component = componentMap?.get(
    qualify(Plugins.name('__SitePlugin'), Component.name(componentId))
  );
  if (!component) return EMPTY_SCENARIO_LIST;
  const scenarios = Component.getPreviewScenarios(component);
  if (!scenarios) return EMPTY_SCENARIO_LIST;
  const variants = Component.getVariants(component);
  return variants ? withoutVariantProps(scenarios)(variants) : scenarios;
};

/**
 * Returns `true` when a component has recorded preview states but its
 * `source.code` has changed since those states were produced — a likely
 * sign the states are stale and should be regenerated.
 *
 * Returns `false` when the component has no states, no source, or no
 * recorded `statesSourceHash` (e.g. states stored before staleness
 * tracking existed) — we never flag staleness we can't substantiate, to
 * avoid false positives.
 */
export const selectAreComponentStatesStale = (
  state: {DesignerStore: DesignerStateType},
  componentId: string | null | undefined
): boolean => {
  if (!componentId) return false;
  const componentMap = getLiveComponentMap(state.DesignerStore);
  const component = componentMap?.get(
    qualify(Plugins.name('__SitePlugin'), Component.name(componentId))
  );
  if (!component) return false;
  const source = Component.getSource(component);
  const states = source?.meta?.states;
  if (!source || !states || states.length === 0) return false;
  const recordedHash = source.meta?.statesSourceHash;
  if (recordedHash == null) return false;
  return hashCode(source.code) !== recordedHash;
};

/**
 * When focused inside a code component in context (via symbolEditStarted)
 * on a regular page (not on the component canvas), returns the component's
 * ID and source code. Returns null otherwise.
 */
export const selectFocusedCodeComponentContext = createSelector(
  selectCurrentComponent,
  selectCurrentComponentType,
  selectIsUserComponentFocused,
  selectCurrentComponentCanvasPage,
  (
    currentComponent,
    currentComponentType,
    isUserComponentFocused,
    currentComponentCanvasPage
  ) => {
    if (currentComponentCanvasPage) return null;
    if (!isUserComponentFocused) return null;

    const source = Component.getSource(currentComponent);
    if (!source) return null;

    const componentId = getName(currentComponentType);
    const displayName = Component.getDisplayName(currentComponent);
    return {id: componentId, code: source.code, displayName};
  }
);

/**
 * Every code component on the site paired with its main source.
 *
 * Source files are site-scoped, so a module can be imported by more than one
 * component; this is what lets callers work out which files are shared before
 * an edit, rename, or delete silently affects another component.
 *
 * Memoized on the component map itself rather than through `createSelector` on
 * the whole DesignerStore, so it only recomputes when components actually
 * change.
 */
export const selectSiteCodeComponentSources = createSelector(
  (state: {DesignerStore: DesignerStateType}) =>
    getLiveComponentMap(state.DesignerStore),
  (components): Array<{id: string; name: string; code: string}> =>
    components.reduceAtPluginName(
      Plugins.name('__SitePlugin'),
      (acc, component, componentName) => {
        const source = Component.getSource(component);
        // `componentName` is the component's key under the plugin, which is the
        // componentId the canvas selectors expose -- the identity callers
        // compare on, since display names are not unique. The display name
        // rides along because that is what the tree shows.
        if (source) {
          acc.push({
            id: String(componentName),
            name: Component.getDisplayName(component),
            code: source.code,
          });
        }
        return acc;
      },
      [] as Array<{id: string; name: string; code: string}>
    )
);
